import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import moment from 'moment'

dayjs.extend(utc)

export const getFormatedDate = (hasTime: boolean, needLocalize: boolean, date?: string) => {
  if (date === undefined || date === null) return ''
  const format = hasTime ? 'MM/DD/YYYY h:mm A' : 'MM/DD/YYYY'
  return needLocalize ? moment.utc(date)?.local()?.format(format) : moment.utc(date)?.format(format)
}

export const getFormatedDateWithoutTimezone = (date: string) => {
  const value = new Date(date)
  const month =
    (parseInt(value.getMonth().toString(), 10) + 1).toString().length === 1
      ? `0${(parseInt(value.getMonth().toString(), 10) + 1).toString()}`
      : parseInt(value.getMonth().toString(), 10) + 1

  const day = value.getDate().toString().length === 1 ? `0${value.getDate()}` : value.getDate()

  const fullYear = value.getFullYear()
  // appends 'T00:00:00.000' for removing time and time zone
  return `${fullYear}-${month}-${day}T00:00:00.000Z` as unknown as Date
}

export const getFormatedDateWithoutTimezoneReturnString = (date: string): string => {
  const value = new Date(date)
  const month = value.getMonth() + 1 < 10 ? `0${value.getMonth() + 1}` : `${value.getMonth() + 1}`
  const day = value.getDate() < 10 ? `0${value.getDate()}` : `${value.getDate()}`
  const fullYear = value.getFullYear()

  // Returns ISO-like string but ensures no time zone
  return `${fullYear}-${month}-${day}T00:00:00.000Z`
}

// Returns a timezone-independent YYYY-MM-DD string for AI-vs-form date comparisons.
// - YYYY-MM-DD or YYYY-MM-DDT... strings are sliced so the calendar day from the API
//   is preserved without re-parsing through new Date('YYYY-MM-DD') (UTC-midnight) and
//   local-time getters — the cross-TZ shift that caused the May 2026 DOB false-positive.
// - Date instances and Date.toString() strings format via dayjs in local time, matching
//   the calendar day the user picked.
// Mirrors the inline `getDobYmd` introduced in commit 455221101.
export const getYmd = (value: unknown): string => {
  if (value == null || value === '') return ''
  if (value instanceof Date) {
    // eslint-disable-next-line no-restricted-globals
    return isNaN(value.getTime()) ? '' : dayjs(value).format('YYYY-MM-DD')
  }
  const s = String(value)
  if (!s || s === 'Invalid Date') return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  // eslint-disable-next-line no-restricted-globals
  const d = new Date(s)
  // eslint-disable-next-line no-restricted-globals
  return isNaN(d.getTime()) ? '' : dayjs(d).format('YYYY-MM-DD')
}

export const removeTimeFromDate = (date: Date) => date.toString().split('T')[0] as unknown as Date

export const extractTimeFromDate = (date: string): string =>
  new Date(date).toLocaleTimeString('en-US', { hour12: false })

export const calculateAge = (dob: Date) => {
  const birthDate = moment(dob)
  const currentDate = moment()
  return currentDate.diff(birthDate, 'years')
}

export const getDateFormatWithTextMonthAndTime = (dateString: string) =>
  moment(dateString).format('MMM DD, YYYY hh:mm A')

// UTC-aware variant: backend timestamps are stored as naked UTC, so they must be
// read as UTC and converted to local. Used by the activity log so its time aligns
// with the threshold sand-timer tooltip (which measures elapsed from a UTC instant).
export const getDateFormatWithTextMonthAndTimeUtc = (dateString: string) =>
  moment.utc(dateString).local().format('MMM DD, YYYY hh:mm A')

export const getFormatedUTCDateWithoutTimezone = (date: string): string => {
  if (!date || typeof date !== 'string') return ''

  const parsed = dayjs(date)
  if (!parsed.isValid()) return ''

  const utcDate = new Date(Date.UTC(parsed.year(), parsed.month(), parsed.date()))
  return utcDate.toISOString()
}
export const getFormatedDateWithMonthDayTime = (
  hasTime: boolean,
  needLocalize: boolean,
  date?: string,
  hasYear = false,
) => {
  if (!date) return ''
  // hasYear opt-in keeps every existing caller unchanged while letting the
  // required-tasks display show the year, e.g. "Jul 11, 2026 3:05 PM". (16796)
  const datePart = hasYear ? 'MMM D, YYYY' : 'MMM D'
  const format = hasTime ? `${datePart} h:mm A` : datePart
  return needLocalize ? moment.utc(date).local().format(format) : moment.utc(date).format(format)
}

/**
 * Parses a timestamp from the ReferralIQ API as UTC.
 *
 * The notification-rework endpoints serialize UTC without a `Z` suffix
 * ("2026-09-08T14:22:07.418"), which dayjs would otherwise read as local time — shifting
 * every timestamp by the viewer's offset and mis-ordering anything sorted on it.
 * @param isoDate timestamp from an API response.
 * @returns a dayjs in local time, or null when the value is missing or unparseable.
 */
export const parseApiUtc = (isoDate?: string | null): Dayjs | null => {
  if (!isoDate) return null

  // An explicit offset means the value already carries its zone; leave it alone.
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(isoDate)
  const parsed = hasOffset ? dayjs(isoDate) : dayjs.utc(isoDate)

  return parsed.isValid() ? parsed.local() : null
}
